import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  IconButton,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { fetchTeamDetails } from "../../../../Utils/AuctionFeatureUtils/AuctionUtils";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import '../../../../assets/styles/Bidding/TeamsTab.css';
import { BACKEND_DOMAIN } from "../../../../common/env";

const TeamsTab = () => {
  const [teams, setTeams] = useState([]);
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { auctionId } = useParams();
  const [imagesLoaded, setImagesLoaded] = useState(false);

  useEffect(() => {
    const fetchTeamsAndAuction = async () => {
      try {
        // Fetch auction details to get playersPerTeam
        const token = localStorage.getItem("token");
        const auctionResponse = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAuction(auctionResponse.data);

        const fetchedTeamDetails = await fetchTeamDetails(auctionId);
        // Initialize each team with an empty players array and iconPlayers array
        const teamsWithPlayers = fetchedTeamDetails.map(team => ({
          ...team,
          players: team.players || [],
          iconPlayers: team.iconPlayers || []
        }));
        setTeams(teamsWithPlayers);
      } catch (error) {
        console.error("Error fetching teams or auction:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamsAndAuction();
  }, [auctionId]);

  // Fetch players for all teams when component mounts
  useEffect(() => {
    const fetchAllPlayers = async () => {
      if (teams.length > 0) {
        const updatedTeams = [...teams];
        const token = localStorage.getItem("token");
        
        try {
          // Fetch players for each team
          for (let i = 0; i < updatedTeams.length; i++) {
            const team = updatedTeams[i];
            const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/teams/${team._id}/players`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            
            // Calculate total spent by the team
            const players = response.data.players || [];
            const totalSpent = players.reduce((sum, player) => {
              const amount = player.soldAmount || player.currentBid || player.baseValue || 0;
              return sum + amount;
            }, 0);
            
            updatedTeams[i] = {
              ...team,
              players: players,
              totalSpent: totalSpent
            };
          }
          
          setTeams(updatedTeams);
        } catch (error) {
          console.error("Error fetching players:", error);
        }
      }
    };

    fetchAllPlayers();
  }, [teams.length, auctionId]);

  const filteredTeams = teams.filter(
    (team) =>
      team.teamName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (team.ownerName && team.ownerName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Function to preload images (team logo and player profile pictures)
  const preloadImages = (team) => {
    if (!team || !team.players) return;
    
    const images = [];
    
    // Preload team logo if available
    if (team.teamLogo) {
      const teamLogo = new Image();
      teamLogo.src = `${team.teamLogo}`;
      images.push(teamLogo);
    }

    // Preload player images if available
    team.players.forEach((player) => {
      if (player.playerLogo) {
        const playerImage = new Image();
        playerImage.src = `${player.playerLogo}`;
        images.push(playerImage);
      }
    });

    // When all images are loaded, update the imagesLoaded state to true
    Promise.all(
      images.map((img) =>
        new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve; // Resolve even if image fails to load
        })
      )
    ).then(() => setImagesLoaded(true));
  };

  const generatePDF = async (team) => {
    if (!team) return;
  
    // Wait for images to load before generating PDF
    await preloadImages(team);
  
    const input = document.getElementById(`teamDetailsPDF-${team._id}`);
  
    // Ensure the entire content is rendered by increasing height dynamically
    const canvas = await html2canvas(input, {
      scale: 2, 
      useCORS: true,
      scrollX: 0,
      scrollY: -window.scrollY, // Adjust for scrolling issues
      windowWidth: document.documentElement.offsetWidth,
      windowHeight: document.documentElement.scrollHeight
    });
  
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
  
    const imgWidth = 210; // A4 width in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width; // Maintain aspect ratio
  
    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
    pdf.save(`${team.teamName}_Details.pdf`);
  };
  

  if (loading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress size={50} />
      </Box>
    );

  if (teams.length === 0) {
    return (
      <Typography
        variant="h5"
        fontWeight="bold"
        style={{ marginTop: "20px", textAlign: "center" }}
      >
        No teams found for this auction.
      </Typography>
    );
  }

  return (
    // Updated to work with the new table-like layout - removed padding
    <Box
      className="teams-tab-container"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        width: "100%",
        backgroundColor: "#ffffff",
        padding: 0,
        margin: 0
      }}
    >
      {/* Search Bar - Centered with top margin */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        width: '100%',
        maxWidth: "1200px",
        margin: "16px auto 10px", // Added top margin
        position: "relative",
        top: 0,
        padding: { xs: '0 16px', sm: 0 }
      }}>
        <TextField
          label="Search Teams"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ 
            width: { xs: "200px", sm: "200px" },
            maxWidth: { xs: "200px", sm: "200px" },
            '& .MuiInputBase-root': {
              height: '40px', // Make the input smaller
              alignItems: 'center'
            },
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px' // More rounded corners
            },
            '& .MuiInputLabel-root': {
              transform: 'translate(14px, 10px) scale(1)', // Better label alignment
              fontSize: '0.9rem'
            },
            '& .MuiInputLabel-shrink': {
              transform: 'translate(14px, -6px) scale(0.75)' // Proper shrink alignment
            }
          }}
        />
      </Box>

      {/* Teams Grid - Adjusted container width to center cards more closely */}
      {filteredTeams.length > 0 ? (
        <Grid container spacing={2} sx={{ 
          width: "100%", 
          mt: 0,
          maxWidth: "1200px",
          margin: "0 auto",
          padding: { xs: 1, sm: 0 }
        }}>
          {filteredTeams.map((team) => (
            <Grid
              item
              xs={12}
              sm={6}
              key={team._id}
              className="teams-grid"
              sx={{ 
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              {/* Team Card with Player Table */}
              <Card className={'team-card'} id={`teamDetailsPDF-${team._id}`} sx={{ width: '100%' }}>
                <Box sx={{ padding: 2 }}>
                  {/* Team Logo at the top */}
                  {team.teamLogo && (
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                      <img 
                        src={`${team.teamLogo}`} 
                        alt={team.teamName} 
                        style={{ 
                          width: "80px", 
                          height: "80px", 
                          borderRadius: "50%", 
                          objectFit: "cover",
                          border: "3px solid #e0e0e0",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)"
                        }} 
                      />
                    </Box>
                  )}
                  
                  {/* Team Name */}
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: "1.4rem", md: "1.6rem" }, // Increased font size
                      color: "#3f51b5",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      textAlign: "center",
                      mb: 2
                    }}
                  >
                    {team.teamName}
                  </Typography>
                  
                  {/* Financial Information in a Row */}
                  <Box sx={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    backgroundColor: "#f5f7ff",
                    padding: "12px 16px", // Increased padding
                    borderRadius: "8px",
                    mb: 2,
                    flexWrap: "wrap"
                  }}>
                    <Box sx={{ textAlign: "center", flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold", color: "#555", fontSize: "1rem" }}> {/* Increased font size */}
                        Current Budget
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: "bold", 
                          color: "#d32f2f", // Red color for remaining budget
                          fontSize: "1.2rem" // Increased font size
                        }}
                      >
                        ${team.teamBudget}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ textAlign: "center", flex: 1, borderLeft: "1px solid #ddd", borderRight: "1px solid #ddd" }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold", color: "#555", fontSize: "1rem" }}> {/* Increased font size */}
                        Players Bought
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: "bold", 
                          color: "#3f51b5",
                          fontSize: "1.2rem" // Increased font size
                        }}
                      >
                        {team.players ? team.players.length : 0}{auction && `/${auction.playersPerTeam}`}
                      </Typography>
                    </Box>
                    
                    
                    
                  </Box>

                  {/* Players Table - Uncommented */}
                  {team.players && team.players.length > 0 ? (
                    <Table sx={{ minWidth: 300 }} size="small" aria-label="players table">
                      <TableHead>
                        <TableRow>
                          <TableCell></TableCell>
                          <TableCell sx={{ fontWeight: "bold", fontSize: "1rem", padding: "8px 10px" }}>Player</TableCell> {/* Increased font size */}
                          <TableCell align="center" sx={{ fontWeight: "bold", fontSize: "1rem", padding: "8px 10px" }}>Amount</TableCell> {/* Increased font size */}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {team.players.map((player) => {
                          // Check if player is an icon player for this team
                          const isIconPlayer = team.iconPlayers && team.iconPlayers.some(iconPlayer => iconPlayer._id === player._id);
                          
                          return (
                            <TableRow
                              key={player._id}
                              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                              <TableCell component="th" scope="row" sx={{ width: "60px", minWidth: "60px" }}>
                                <Box sx={{ display: "flex", justifyContent: "center", mb: 2, minWidth: "50px" }}>
                                  <img 
                                    src={`${player.playerLogo}`} 
                                    alt={player.playerName} 
                                    className="player-avatar-mobile"
                                    style={{ 
                                      width: "50px", 
                                      height: "50px", 
                                      borderRadius: "50%", 
                                      objectFit: "cover",
                                      border: "3px solid #e0e0e0",
                                      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                                      flexShrink: 0
                                    }} 
                                  />
                                </Box>

                              </TableCell>
                              <TableCell component="th" scope="row" sx={{ fontSize: "1rem", padding: "8px 10px" }}> {/* Increased font size */}
                                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                  {player.playerName && typeof player.playerName === 'string' ? player.playerName : 'Unknown Player'}
                                  {isIconPlayer && (
                                    <span style={{ marginLeft: '8px', color: '#FFD700', fontSize: '1.2rem' }}>★</span>
                                  )}
                                </Typography>
                              </TableCell>
                              {/* <TableCell align="center" sx={{ fontSize: "1rem", padding: "8px 10px" }}> {/* Increased font size */}
                                {/* <Typography variant="body2">
                                  {player.playerType && typeof player.playerType === 'string' ? player.playerType : 'Unknown Type'}
                                </Typography>
                              </TableCell> */}
                              <TableCell align="center" sx={{ fontSize: "1rem", padding: "8px 10px" }}> {/* Increased font size */}
                                {isIconPlayer ? (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: "bold", 
                                      color: "#000000", // Changed to black
                                      backgroundColor: "#FFF9C4",
                                      padding: "4px 10px",
                                      borderRadius: "12px",
                                      display: "inline-block",
                                      fontSize: "1rem"
                                    }}
                                  >
                                    ICON
                                  </Typography>
                                ) : (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: "bold", 
                                      color: "#3f51b5",
                                      backgroundColor: "#e3f2fd",
                                      padding: "4px 10px", // Increased padding
                                      borderRadius: "12px",
                                      display: "inline-block",
                                      fontSize: "1rem" // Increased font size
                                    }}
                                  >
                                    ${player.soldAmount || player.currentBid || player.baseValue || 0}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <Box
                      sx={{
                        textAlign: "center",
                        padding: 2,
                        borderRadius: "8px",
                        backgroundColor: "#f5f7ff",
                        width: "100%",
                        marginTop: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: "1.1rem", color: "#666" }}> {/* Increased font size */}
                        No players assigned to this team yet.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography className={`no-teams-message ${window.innerWidth >= 600 ? "sm" : ""}`}>
          No team matches your search.
        </Typography>
      )}
    </Box>
  );
};

export default TeamsTab;